<script setup>
import { computed, ref, toRaw, onMounted } from 'vue';
import { useRoute } from 'vue-router';
import { useQuery } from 'villus';
import { create, fractionDependencies, formatDependencies } from 'mathjs';
import remove from 'lodash-es/remove';
import startsWith from 'lodash-es/startsWith';
import each from 'lodash-es/each';
import isEmpty from 'lodash-es/isEmpty';
import map from 'lodash-es/map';
import humanizeDuration from 'humanize-duration';
import Flexible from '@/components/flexible/Flexible.vue';
import FlexibleQuery from '@/components/flexible/FlexibleQuery';
import PartnerWith from '@/components/partner-with/PartnerWith.vue';
import DiscountsBtnPill from '@/components/discounts-cta/DiscountsBtnPill.vue';
import PartnerFragment from '@/graphql/PartnerFragment';
import PartnerLink from '@/components/partner-link/PartnerLink.vue';
import Share from '@/components/share/Share.vue';
import ProductCta from '@/components/product-cta/ProductCta.vue';
import DiscountsCta from '@/components/discounts-cta/DiscountsCta.vue';
import ad from '@/components/flexible/sections/ad.vue';
import Newsletter from '@/components/newsletter/Newsletter.vue';
import { useStore } from '@/store/Store.js';
import { getClient } from '@/core/js/GraphqlClient.js';
import FeaturedGrid from '@/components/featured-grid/FeaturedGrid.vue';
import Header from '@/components/header/Header.vue';
import { useLenis } from '@/components/scroll/Lenis.js';

import Whisk from '@/icons/whisk.svg';
import Cooking from '@/icons/cooking.svg';
import Time from '@/icons/time.svg';
import Portions from '@/icons/portions.svg';
import Print from '@/icons/printer.svg';

import BtnPill from '@/components/btn/BtnPill.vue';

const lenis = useLenis();
const store = useStore();
const route = useRoute();
const routeSlug = route.params.slug;
const recipeDetails = ref({});
const astuces = ref([]);
const ingredientIds = ref([]);
const adFilters = ref({});
const discountsEl = ref(null);
const recipeDetailsSplit = ref({});
const crumbs = ref([]);
const mounted = ref(false);
const rootEl = ref(null);
const client = getClient();
const FRMeasures = ref({
    tsp: ['cuiller à thé', 'cuillers à thé'],
    tbsp: ['cuiller à table', 'cuillers à table'],
    floz: ['once liquide', 'onces liquides'],
    cups: ['tasse', 'tasses'],
    oz: ['onces', 'onces'],
    lb: ['livre', 'livres'],
    ml: ['millilitre', 'millilitres'],
    l: ['litre', 'litres'],
    mg: ['milligramme', 'milligrammes'],
    g: ['grammes', 'gramme'],
    kg: ['kilogramme', 'kilogrammes'],
});
const variables = computed(() => ({
    lang: store.lang.value,
    slug: routeSlug,
}));

const formatNum = (single, plural, number) => (number >= 2 ? plural : single);

const formatUnit = (ingredient) => {
    if (ingredient.units === '') {
        return '';
    }
    const num = parseFloat(ingredient.quantity);
    const unitKey = ingredient.units;
    const fr = FRMeasures.value[unitKey];

    if (fr.length === 1) {
        fr[1] = fr[0];
    }

    return formatNum(...fr, num);
};

const { fraction } = create({
    fractionDependencies,
    formatDependencies,
});

/**
 * Format some numbers in Recipe.vue correctly when they're fractions formatted by our custom function
 * Will only be used / triggered when a fraction doesn't have an HTML character.
 *
 * Ex.: 3/4 exists as &frac34; which bypasses our function but 3/7 doesn't, so we receive this html code <sup>3</sup>/<sub>7</sub>
 * @see vue/src/commons/css/components.css
 */
const formatFraction = (str) => {
    str = str.replace(/\.33?/, '.(3)');
    str = str.replace(/\.66?/, '.(6)');

    const mystr = fraction(str).toFraction(true);

    const int = mystr.split(' ');
    let ret = mystr;
    let fracpart = '';
    let intpart = '';
    if (int.length === 1) {
        fracpart = int[0];
        intpart = '';
    } else {
        fracpart = int[1];
        intpart = `${int[0]} `;
    }
    if (fracpart.indexOf('/') !== -1) {
        const parts = fracpart.split('/');
        if (parts[0] === 1 && (parts[1] === 2 || parts[1] === 4)) {
            // 1/2, 1/4
            ret = `&frac1${parts[1]};`;
        } else if (parts[0] === 3 && parts[1] === 4) {
            // 3/4
            return '&frac34;';
        } else {
            if (parts[1] === 8) {
                if (parts[0] === 1)
                    ret = '&#8539;'; // 1/8
                else if (parts[0] === 3)
                    ret = '&#8540;'; // 3/8
                else if (parts[0] === 5)
                    ret = '&#8541;'; // 5/8
                else if (parts[0] === 7) ret = '&#8542;'; // 7/8
            }

            // else it's a crazy fraction, but we can handle that:
            ret = `<sup>${parts[0]}</sup>&frasl;<sub>${parts[1]}</sub>`;
        }
    }

    return intpart + ret;
};

const shortFrenchHumanizer = humanizeDuration.humanizer({
    language: 'shortFR',
    delimiter: ' ',
    spacer: ' ',
    languages: {
        shortFR: {
            y: () => 'an',
            mo: () => 'mois',
            w: () => 'sem.',
            d: () => 'jours',
            h: () => 'h',
            m: () => 'min',
            s: () => 's',
            ms: () => 'ms',
        },
    },
});

const formatTime = (minutes) => {
    const minuteInMs = 60000;
    return shortFrenchHumanizer(minutes * minuteInMs);
};

const print = () => {
    window.print();
};

const isSectionSeparator = (str) => startsWith(str, '-');

const scrollToDiscounts = () => {
    lenis.value.scrollTo(discountsEl.value, {
        duration: 1.5,
    });
};

const getRecipeDetails = async (link) => {
    const resLink = await fetch(link);
    const json = await resLink.json();

    /**
     * Elements starting with '*' are considered tips
     */
    if (json.recetteContenu.directions) {
        const res = remove(json.recetteContenu.directions, (el) => startsWith(el.direction, '*'));
        each(res, (el) => {
            el.direction = el.direction.replace(/^\*\s?(.*)/, '$1');
        });
        astuces.value = res;
    }

    /**
     * Elements starting with '-' are considered a new step
     * Let's split into arrays to guarantee ordering, instead of using object keys
     *
     * Sometimes an object is parsed by the ajax response instead of an array somehow
     * so we check is empty with lodash.
     */
    if (json.recetteContenu.ingredients && !isEmpty(json.recetteContenu.ingredients)) {
        let currStep = 'none';
        const details = [];
        let detailsStep = { step: 'none', ingredients: [] };
        each(json.recetteContenu.ingredients, (el, key) => {
            if (isSectionSeparator(el.ingredient)) {
                if (key !== 0) {
                    details.push(detailsStep);
                }
                currStep = el.ingredient.replace(/^-\s?/, '');
                detailsStep = { step: currStep, ingredients: [] };
            } else {
                detailsStep.ingredients.push(el);
            }
        });
        details.push(detailsStep);
        recipeDetailsSplit.value = details;
    }

    recipeDetails.value = json.recetteContenu;
};

const recipeTypesColorMap = {
    vegetarien: '#66983F',
    'sans-gluten': '#DC9E2E',
    'sans-lactose': '#F1716F',
    'sans-noix': '#DC9E2E',
    vegane: '#66983F',
    'faible-en-calories': '#33567B',
    antioxydants: '#F1716F',
};

const postFields = `
uri
title
typeHandle,
... on recette_Entry {
    category: recetteCategory { title },
    cover: recetteCover { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
... on blog_Entry {
    day: postDate @formatDateTime (format: "d"),
    month: postDate @formatDateTime (format: "n"),
    year: postDate @formatDateTime (format: "Y"),
    category: blogCategory { title },
    cover: blogCover { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
... on product_Entry {
    cover: flexibleImage { title, url @transform (format: "webp", quality: 80, width: 800, height:426, immediately: true) },
}
`;

const qq = `
    query FetchEntries($lang: [String], $slug: [String]) {
        recettes: entry(section: "recettes", site: $lang) {
            sectionHandle
            ... on recettes_Entry { 
                back: recettesBackCta
                subtitle: pancarteHautTitreBasSousPage
                suptitle: pancarteHautTitreHautSousPage
                subsubtitle: pancarteBasTitreBasSousPage
                supsubtitle: pancarteBasTitreHautSousPage
                personnageEntete: personnageEnteteSousPage {
                    inline
                }
            }
        },
        entry (section: "recette", site: $lang, slug: $slug) {
            id,
            title,
            ... on recette_Entry {
                category: recetteCategory { id, url, ... on recettescategories_default_Entry { title, slug, uri } },
                type: recetteTypeMain { id, ... on recettestypes_default_Entry { slug, uri, title } },
                typeSec: recetteType { id, ... on recettestypes_default_Entry { slug, uri, title } },
                relatedContents { 
                    ${postFields}
                }
                displayRelatedContents
                ${PartnerFragment('partner')},
                ${FlexibleQuery()},
                ${FlexibleQuery('flexiblealt')},
                cover: recetteCover {
                    url @transform(format: "webp", quality: 80, width: 2000, immediately: true)
                },
                ingredients: recetteIngredients { id, title }
            }
        },
        measures: globalSet(handle: "recipe") {
            ... on recipe_GlobalSet {
                tsp: globalsRecipeTSP,
                tbsp: globalsRecipeTBSP,
                floz: globalsRecipeFLOZ,
                cups: globalsRecipeCUPS,
                oz: globalsRecipeOZ,
                lb: globalsRecipeLB,
                ml: globalsRecipeML,
                l: globalsRecipeL,
                mg: globalsRecipeMG,
                g: globalsRecipeG,
                kg: globalsRecipeKG
            }
        }
    }
`;

const { data } = await useQuery({
    variables,
    query: qq,
});

if (data?.value?.entry?.category?.[0]?.url) {
    crumbs.value = [
        { url: '/recettes', text: 'Recettes' },
        { url: data.value.entry.category[0].url, text: data.value.entry.category[0].title },
    ];
}

let ings = [];
if (data?.value?.entry?.ingredients) {
    ings = ings.concat(map(data.value.entry.ingredients, 'id'));
}
ingredientIds.value = ings;
adFilters.value.relatedTo = ings;
adFilters.value.contentType = 'recette';
if (data.value.entry.type && data.value.entry.type.length !== 0) {
    adFilters.value.relatedTo = adFilters.value.relatedTo.concat(map(data.value.entry.type, 'id'));
}

/**
 * Recommendations random
 */
const variablesRecommendation = computed(() => ({
    lang: store.lang.value,
    ingredientIds: ings,
}));

const qq2 = `
    query FetchAutomaticRelatedEntries($lang: [String], $ingredientIds: [QueryArgument]) {
        recommendations: entries(section: ["recette", "blog", "product"], site: $lang, relatedTo: $ingredientIds, orderBy:"RAND()", limit: 6) {
            ${postFields}
        }
    }
`;
const { data: dataRecommendation } = await useQuery({
    variables: variablesRecommendation,
    query: qq2,
});
/**
 * End recommendations random
 */

if (data && data.value && data.value.measures) {
    const keys = Object.keys(data.value.measures);
    for (let i = 0, il = keys.length; i < il; ++i) {
        const measureKey = keys[i];
        if (data.value.measures[measureKey]) {
            FRMeasures.value[measureKey] = data.value.measures[measureKey].split(',').map((item) => item.trim());
        }
    }
}

await getRecipeDetails(`/recipe-details/${data.value.entry.id}?lang=${store.lang.value}`);

onMounted(() => {
    store.setHeaderColor({
        mainColor: 'indigo',
        theme: 'dark',
        wave: 'light-indigo',
        normal: {
            bg: 'white',
            txt: 'dark-indigo',
        },
        over: {
            bg: 'white',
            txt: 'dark-indigo',
        },
        prenav: {
            bg: 'bg-dark-indigo',
            txt: 'text-white',
        },
        nav: {
            txt: 'text-white',
            hover: 'bg-dark-indigo',
            burger: 'text-white',
            lines: 'text-dark-indigo',
        },
    });

    mounted.value = true;
});

const recommendations = computed(() => {
    let results = [];
    if (data?.value?.entry?.relatedContents?.length > 0) {
        results = data.value.entry.relatedContents;
    } else if (dataRecommendation?.value?.recommendations?.length > 0) {
        results = dataRecommendation.value.recommendations;
    }
    return results;
});
</script>

<template>
    <div
        ref="rootEl"
        class="Recipe bg-bright-indigo print:bg-white print:text-dark-gray"
        :class="[pageClasses]"
    >
        <template v-if="mounted && data && recipeDetails">
            <Header
                :data="data.recettes"
                color="dark-blue"
                title-color="dark-indigo"
                :suptitle="data?.entry?.category.length > 0 ? data.entry.category[0].title : null"
                :title="data.entry.title"
                :crumbs="crumbs"
            />
            <div class="Recipe__inner relative z-10">
                <div class="Recipe__page-top relative mx-auto max-w-5xl px-6 text-center print:w-auto">
                    <div class="Recipe__bottom flex justify-center print:mt-2 print:block">
                        <div class="Recipe__bottom-left flex flex-col text-sm print:inline-block md:flex-row md:flex-wrap lg:justify-center xl:text-lg">
                            <template v-if="recipeDetails.prepTime">
                                <span class="Recipe__detail mb-4 flex items-baseline whitespace-nowrap print:inline-block md:mr-8">
                                    <span class="Recipe__detail-label inline-flex w-1/2 items-baseline print:w-auto md:w-32 md:w-auto">
                                        <Whisk class="mr-2 w-auto fill-current font-medium text-light-red em:h-4 print:text-dark-gray" />
                                        <span class="mr-4 text-light-red print:text-dark-gray">Préparation:</span>
                                    </span>
                                    <span class="font-bold text-dark-indigo print:text-dark-gray">{{ formatTime(recipeDetails.prepTime) }} </span>
                                </span>
                            </template>
                            <template v-if="recipeDetails.cookTime">
                                <span class="Recipe__detail mb-4 flex items-baseline whitespace-nowrap print:inline-block md:mr-8">
                                    <span class="Recipe__detail-label inline-flex w-1/2 items-baseline print:w-auto md:w-32 md:w-auto">
                                        <Cooking class="mr-2 w-auto fill-current font-medium text-light-red em:h-4 print:text-dark-gray" />
                                        <span class="mr-4 text-light-red print:text-dark-gray">Cuisson:</span>
                                    </span>
                                    <span class="font-bold text-dark-indigo print:text-dark-gray">{{ formatTime(recipeDetails.cookTime) }} </span>
                                </span>
                            </template>
                            <template v-if="recipeDetails.totalTime">
                                <span class="Recipe__detail mb-4 flex items-baseline whitespace-nowrap print:inline-block md:mr-8">
                                    <span class="Recipe__detail-label inline-flex w-1/2 items-baseline print:w-auto md:w-32 md:w-auto">
                                        <Time class="mr-2 w-auto fill-current font-medium text-light-red em:h-4 print:text-dark-gray" />
                                        <span class="mr-4 font-medium text-light-red print:text-dark-gray">Total:</span>
                                    </span>
                                    <span class="font-bold text-dark-indigo print:text-dark-gray">{{ formatTime(recipeDetails.totalTime) }}</span>
                                </span>
                            </template>
                            <template v-if="recipeDetails.serves">
                                <span class="Recipe__detail mb-4 flex items-baseline print:inline-block md:mr-8 md:whitespace-nowrap">
                                    <span class="Recipe__detail-label inline-flex w-1/2 items-baseline print:w-auto md:w-32 md:w-auto">
                                        <Portions class="mr-2 w-auto fill-current font-medium text-light-red em:h-4 print:text-dark-gray" />
                                        <span class="mr-4 font-medium text-light-red print:text-dark-gray">Portions:</span>
                                    </span>
                                    <span class="w-1/2 text-left font-bold text-dark-indigo print:text-dark-gray md:w-auto md:text-center">
                                        {{ recipeDetails.serves }}
                                        {{ recipeDetails.servesUnit == '' ? 'portions' : recipeDetails.servesUnit }}
                                    </span>
                                </span>
                            </template>
                            <template v-if="data.entry.type">
                                <div class="Recipe__detail mb-4 mr-8 flex w-full flex-wrap items-baseline justify-center gap-2 whitespace-nowrap print:hidden">
                                    <template
                                        v-for="(type, index) in data.entry.type"
                                        :key="index"
                                    >
                                        <BtnPill
                                            :icon="type.slug + '-small'"
                                            :to="setUri({ uri: type.uri })"
                                            class="!mb-0 !md:mb-4"
                                            :style="{
                                                backgroundColor: recipeTypesColorMap[type.slug],
                                            }"
                                        >
                                            <template #text>
                                                <span>{{ type.title }}</span>
                                            </template>
                                        </BtnPill>
                                    </template>
                                    <template
                                        v-for="(type, index) in data.entry.typeSec"
                                        :key="index"
                                    >
                                        <BtnPill
                                            :icon="type.slug + '-small'"
                                            :to="setUri({ uri: type.uri })"
                                            class="!mb-0 !md:mb-4"
                                            :style="{
                                                backgroundColor: recipeTypesColorMap[type.slug],
                                            }"
                                        >
                                            <template #text>
                                                <span>{{ type.title }}</span>
                                            </template>
                                        </BtnPill>
                                    </template>
                                </div>
                            </template>
                        </div>
                    </div>
                </div>

                <div class="mx-auto max-w-5xl px-6">
                    <Flexible
                        :remove-space-bottom="true"
                        :ad-filters="adFilters"
                        cover-color="blue"
                        color="blue"
                        :data="data.entry.flexible"
                        :center="true"
                        class="print:hidden print:saturate-0"
                        main-color="blue"
                    />
                    <div class="Recipe__partner print:hidden print:saturate-0">
                        <PartnerWith
                            v-if="data.entry.partner && data.entry.partner.length > 0"
                            :data="data.entry.partner"
                        />
                    </div>

                    <ad
                        v-if="adFilters"
                        color="light-red"
                        :center="true"
                        :data="{ adFilters: adFilters }"
                    />

                    <div
                        v-if="recipeDetailsSplit"
                        class="Recipe__ingredients prose-nums Recipe__section mx-auto my-15 max-w-2xl print:m-0 print:my-[12pt] print:w-full print:max-w-none md:my-26"
                    >
                        <h3 class="h3 em:mb-3 print:text-dark-gray">Ingrédients</h3>
                        <template
                            v-for="(stepObj, index) in recipeDetailsSplit"
                            :key="stepObj.step + index"
                        >
                            <h4
                                v-if="stepObj.step !== 'none'"
                                class="h5 counter-reset-directions w-full text-dark-indigo em:mb-2 print:text-dark-gray"
                            >
                                {{ stepObj.step }}
                            </h4>
                            <ul class="Recipe__ingredients-list Recipe__highlight mb-12 mt-4 columns-1 text-light-red print:text-dark-gray md:columns-2">
                                <template
                                    v-for="(ingredient, ingredientIndex) in stepObj.ingredients"
                                    :key="stepObj.step + '_ingredient_' + ingredientIndex"
                                >
                                    <li
                                        class="Recipe__ingredients-list-item relative break-inside-avoid-column before:absolute before:left-0 before:top-[.3em] before:block before:rounded-full before:border-2 before:text-light-red em:mb-4 em:pl-8 before:em:mr-2 before:em:h-3 before:em:w-3 print:mb-1 print:before:mr-2 print:before:border-dark-gray md:pr-8 before:xl:border-3"
                                    >
                                        <span
                                            v-if="ingredient.quantity"
                                            v-html="formatFraction(ingredient.quantity) + '&nbsp;'"
                                        />
                                        <span v-if="formatUnit(ingredient) != ''">{{ formatUnit(ingredient) }}&nbsp;</span>
                                        <span>{{ ingredient.ingredient }}</span>
                                    </li>
                                </template>
                            </ul>
                        </template>

                        <template v-if="data.entry.ingredients && data.entry.ingredients.length > 0">
                            <DiscountsBtnPill
                                :ingredients="data.entry.ingredients"
                                icon="dollar"
                                color="light-red"
                                :external="true"
                                class="print:hidden"
                                v-on:click="scrollToDiscounts"
                            />
                        </template>
                    </div>

                    <div
                        v-if="recipeDetails.directions && !isEmpty(recipeDetails.directions)"
                        class="Recipe__ingredients prose-nums Recipe__section mx-auto my-15 max-w-2xl print:m-0 print:my-[12pt] print:w-full print:max-w-none md:my-26"
                    >
                        <h3 class="h3 em:mb-3">Préparation</h3>
                        <ol class="Recipe__direction-list counter-reset-directions mt-4">
                            <li
                                v-for="(direction, index) in recipeDetails.directions"
                                :key="index"
                                :class="[
                                    isSectionSeparator(direction.direction)
                                        ? 'Recipe__list-section counter-reset-directions w-full break-inside-avoid text-dark-indigo em:mb-2 print:mb-1 print:text-dark-gray'
                                        : 'Recipe__direction-list-item flex break-inside-avoid em:mb-6 print:mb-1',
                                ]"
                            >
                                <h4
                                    v-if="isSectionSeparator(direction.direction)"
                                    class="h5 mb-4 mt-12"
                                >
                                    {{ direction.direction.replace(/^\-\s?/, '') }}
                                </h4>
                                <template v-else>
                                    <span
                                        class="Recipe__direction-list-number counter-increment-directions print:border-1 mr-8 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-light-red pt-1 text-light-red print:mr-4 print:h-6 print:w-6 print:self-baseline print:border-dark-gray print:pt-0 print:text-dark-gray xl:border-3"
                                    />
                                    <span class="em:mt-1 print:mt-0">{{ direction.direction }}</span>
                                </template>
                            </li>
                        </ol>
                    </div>

                    <div
                        v-if="astuces && astuces.length"
                        class="Recipe__ingredients prose-nums Recipe__section mx-auto my-15 max-w-2xl print:m-0 print:my-[12pt] print:w-full print:max-w-none md:my-26"
                    >
                        <h3 class="h3 em:mb-3">Trucs et astuces</h3>
                        <ul
                            class="Recipe__direction-list"
                            :class="{ 'Recipe__direction-list-half -mx-4 flex flex-wrap': astuces.length > 1 }"
                        >
                            <li
                                v-for="(direction, index) in astuces"
                                :key="index"
                                :class="{ 'px-4': astuces.length > 1 }"
                                class="Recipe__direction-list-item flex w-full font-medium em:mb-6 print:mb-1 print:w-full md:w-1/2"
                            >
                                <span
                                    class="Recipe__direction-list-star mr-8 inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-full border-2 border-dark-blue pt-2 text-dark-blue print:mr-4 print:h-6 print:w-6 print:self-baseline print:border-dark-gray print:pt-2 print:text-dark-gray xl:border-3"
                                    >*</span
                                >
                                <span class="Recipe__highlight text-light-red print:text-dark-gray">{{ direction.direction }}</span>
                            </li>
                        </ul>
                    </div>

                    <Flexible
                        class="mt-15 print:saturate-0 md:mt-0"
                        :remove-space-bottom="true"
                        :ad-filters="adFilters"
                        cover-color="blue"
                        color="yellow"
                        :data="data.entry.flexiblealt"
                        :center="true"
                        main-color="blue"
                    />

                    <PartnerLink
                        v-if="data.entry.partner.length > 0"
                        :data="data.entry.partner"
                    />

                    <div class="Recipe__source mt-12 italic print:saturate-0 xl:mt-22">
                        <small>{{ data.entry.source }}</small>
                    </div>

                    <ad
                        v-if="adFilters"
                        color="light-red"
                        :center="true"
                        :data="{ adFilters: adFilters }"
                    />

                    <Share class="print:hidden" />
                </div>

                <Newsletter />

                <FeaturedGrid
                    v-if="recommendations.length > 0"
                    :tag="true"
                    :posts="recommendations"
                    title-top="D’autres contenus qui pourraient vous intéresser"
                    class="lg:pt-6 xl:pt-0"
                />

                <template v-if="ingredientIds.length">
                    <div ref="discountsEl">
                        <DiscountsCta
                            title-top="Apprenez-en plus sur les produits bio mis en valeur dans cette recette!"
                            :ingredient-ids="ingredientIds"
                            cover-color="blue"
                            class="print:hidden"
                        />
                    </div>
                </template>

                <template v-if="ingredientIds.length">
                    <ProductCta
                        title-top="Apprenez-en plus sur les produits bio mis en valeur dans cette recette!"
                        cta="Voir tous les produits"
                        :ingredient-ids="ingredientIds"
                        bg-color="blue"
                        class="print:hidden lg:pt-10 xl:pt-4"
                    />
                </template>
            </div>
        </template>
    </div>
</template>
